import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Box, IconButton, Typography, Button, LinearProgress, Fade, Backdrop, CircularProgress } from '@mui/material';
import { Close as CloseIcon, Favorite as HeartFilledIcon, Clear as XIcon, Psychology as PsychologyIcon, AutoAwesome as SparkleIcon } from '@mui/icons-material';
import { motion, AnimatePresence, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { QuizResponseType, SwipeDirection, QuizProgress, QuizVenue } from '../../types/interfaces';
import './PreferencesQuizModal.scss';
import { useQuery } from '@tanstack/react-query';
import { APIClient } from 'src/lib/api_client';

interface PreferencesQuizModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const PreferencesQuizModal: React.FC<PreferencesQuizModalProps> = ({ open, onClose, onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentItem, setCurrentItem] = useState<QuizVenue | null>(null);

  // Animation states
  const [exitDirection, setExitDirection] = useState<SwipeDirection | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState<QuizProgress>(
    {
      totalItems: 0,
      answeredCount: 0,
      likesCount: 0,
      dislikesCount: 0,
      skipsCount: 0,
      progressPercentage: 0,
      isComplete: false
    }
  )
  // Motion values for swipe gestures
  const xDisplacement = useMotionValue(0);
  const rotate = useTransform(xDisplacement, [-200, 0, 200], [-15, 0, 15]);
  const opacity = useTransform(xDisplacement, [-200, -100, 0, 100, 200], [0, 0.5, 1, 0.5, 0]);
  const constraintsRef = useRef(null);
  const progressPercentage = progress?.progressPercentage || 0;

  const getQuizItems = async (): Promise<any> => {
    const response = await APIClient.get('/quiz/items/')
    return response.data
  }

  const getQuizProgress = async () => {
    const response = await APIClient.get('/quiz/progress/')
    if (response?.data?.progress) {
      setProgress(response.data.progress);
      setCurrentIndex(response.data.progress.answeredCount);
    }
    return response.data
  }

  const submitResponse = useCallback(async (venue_id: string, choice: QuizResponseType) => {
    const response = await APIClient.post('/quiz/response/', {
      venueId: venue_id,
      response: choice,
    });

    // Update progress from API response
    if (response?.data?.progress) {
      setProgress(response.data.progress);
    }

    return response.data;
  }, []);

  const itemsQuery = useQuery({
    queryKey: ["quizitems"],
    queryFn: getQuizItems,
    staleTime: 10 * 60 * 1000, // 10 minutes - reduce refetching
    gcTime: 30 * 60 * 1000, // 30 minutes 
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    retry: false
  });

  const isCompleted = useCallback(() => {
    return progress?.isComplete || false;
  }, [progress?.isComplete]);

  useEffect(() => {
    setCurrentItem(itemsQuery.data?.venues?.[currentIndex])
  }, [itemsQuery.data?.venues, currentIndex])

  // Handle swipe completion
  const handleSwipeEnd = useCallback(
    async (_info: PanInfo, responseType: QuizResponseType) => {
      if (!currentItem) return;

      try {
        setIsSaving(true)
        await submitResponse(currentItem.id, responseType);
        setCurrentIndex(currentIndex + 1)

        // Animation delay before next venue or completion
        setTimeout(() => {
          xDisplacement.set(0);
          setExitDirection(null);
          setIsAnimating(false);

          // Check if quiz is now completed
          if (isCompleted()) {
            onComplete();
          }
        }, 300);

      } catch (err) {
        console.error('Failed to submit quiz response:', err);
        // Animation will be handled by error state
        setTimeout(() => {
          xDisplacement.set(0);
          setExitDirection(null);
          setIsAnimating(false);
        }, 300);
      } finally {
        setIsSaving(false)
      }
    },
    [currentItem, isCompleted, onComplete, submitResponse, xDisplacement, currentIndex]
  );

  // Handle drag end
  const handleDragEnd = useCallback(
    (_event: any, info: PanInfo) => {
      const threshold = 100;
      const { offset, velocity } = info;

      if (Math.abs(offset.x) > threshold || Math.abs(velocity.x) > 500) {
        const direction = offset.x > 0 ? SwipeDirection.RIGHT : SwipeDirection.LEFT;
        const responseType = direction === SwipeDirection.RIGHT ? QuizResponseType.LIKE : QuizResponseType.DISLIKE;

        setExitDirection(direction);
        handleSwipeEnd(info, responseType);
      } else {
        xDisplacement.set(0);
      }
    },
    [handleSwipeEnd, xDisplacement]
  );

  // const handleButton = useCallback((buttonType) => {
  // }, [[progress]])
  // Button handlers
  const handleLike = useCallback(() => {
    if (isAnimating || !currentItem) return;
    setExitDirection(SwipeDirection.RIGHT);
    handleSwipeEnd({} as PanInfo, QuizResponseType.LIKE);
  }, [isAnimating, currentItem, handleSwipeEnd]);

  const handleDislike = useCallback(() => {
    if (isAnimating || !currentItem) return;
    setExitDirection(SwipeDirection.LEFT);
    handleSwipeEnd({} as PanInfo, QuizResponseType.DISLIKE);
  }, [isAnimating, currentItem, handleSwipeEnd]);

  const handleSkip = useCallback(() => {
    if (isAnimating || !currentItem) return;
    setExitDirection(SwipeDirection.UP);
    handleSwipeEnd({} as PanInfo, QuizResponseType.SKIP);
  }, [isAnimating, currentItem, handleSwipeEnd]);

  // Handle close
  const handleClose = useCallback(() => {
    if (!isAnimating) {
      onClose();
    }
  }, [isAnimating, onClose]);

  // Load progress when modal opens
  useEffect(() => {
    if (open) {
      getQuizProgress();
    }
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!open || isAnimating || isCompleted()) return;

      switch (event.key) {
        case 'ArrowLeft':
          handleDislike();
          break;
        case 'ArrowRight':
          handleLike();
          break;
        case 'ArrowUp':
          handleSkip();
          break;
        case 'Escape':
          handleClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [open, isAnimating, isCompleted, handleClose, handleDislike, handleLike, handleSkip]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}
      className="preferences-quiz-modal"
      disableEscapeKeyDown={isAnimating}
    >
      <Fade in={open}>
        <Box className="preferences-quiz-modal__container">
          {/* Header */}
          <Box className="preferences-quiz-modal__header">
            <Box display="flex" alignItems="center" gap={1}>
              <PsychologyIcon className="preferences-quiz-modal__icon" />
              <Typography variant="h6" className="preferences-quiz-modal__title">
                Personalize Mindara
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              className="preferences-quiz-modal__close-button"
              disabled={isAnimating}
              aria-label="Close quiz"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Progress Bar */}
          <Box className="preferences-quiz-modal__progress">
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              className="preferences-quiz-modal__progress-bar"
            />
            <Typography variant="body2" className="preferences-quiz-modal__progress-text">
              {!isCompleted() && progress.answeredCount > 0 && (
                <span style={{ color: '#f06ef5', fontWeight: 'bold', marginRight: '8px' }}>
                  Resuming from #{progress.answeredCount + 1}
                </span>
              )}
              Swiped: <span className="preferences-quiz-modal__accent">{progress.answeredCount}</span>/{progress.totalItems} |
              Likes: <span className="preferences-quiz-modal__accent">{progress.likesCount}</span> |
              Dislikes: <span className="preferences-quiz-modal__accent">{progress.dislikesCount}</span>
              {progress.skipsCount > 0 && (
                <> | Skips: <span className="preferences-quiz-modal__accent">{progress.skipsCount}</span></>
              )}
            </Typography>
          </Box>

          {/* Quiz Content */}
          <Box className="preferences-quiz-modal__content" ref={constraintsRef}>
            {itemsQuery.isLoading ? (
              <Box className="preferences-quiz-modal__loading">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <SparkleIcon sx={{ fontSize: 48, color: '#f06ef5' }} />
                </motion.div>
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Loading amazing venues for you...
                </Typography>
              </Box>
            ) : isCompleted() ? (
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="preferences-quiz-modal__completion"
              >
                <Box className="preferences-quiz-modal__completion-content">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <SparkleIcon sx={{ fontSize: 64, color: '#f06ef5', mb: 2 }} />
                  </motion.div>
                  <Typography variant="h5" className="preferences-quiz-modal__completion-title">
                    Preferences Saved!
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', opacity: 0.8 }}>
                    We've learned your taste and will personalize your recommendations accordingly.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleClose}
                    className="preferences-quiz-modal__continue-button"
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={20} /> : undefined}
                  >
                    {isSaving ? 'Saving...' : 'Continue to Settings'}
                  </Button>
                </Box>
              </motion.div>
            ) : currentItem ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentItem.id}
                  className="preferences-quiz-modal__card-container"
                  style={{ x: xDisplacement, rotate, opacity }}
                  drag="x"
                  dragConstraints={constraintsRef}
                  dragElastic={0.7}
                  onDragEnd={handleDragEnd}
                  animate={
                    exitDirection
                      ? {
                        x: exitDirection === SwipeDirection.LEFT ? -1000 :
                          exitDirection === SwipeDirection.RIGHT ? 1000 : 0,
                        y: exitDirection === SwipeDirection.UP ? -1000 : 0,
                        opacity: 0,
                        scale: 0.8,
                      }
                      : { x: 0, y: 0, opacity: 1, scale: 1 }
                  }
                  exit={{
                    x: exitDirection === SwipeDirection.LEFT ? -1000 :
                      exitDirection === SwipeDirection.RIGHT ? 1000 : 0,
                    y: exitDirection === SwipeDirection.UP ? -1000 : 0,
                    opacity: 0,
                    scale: 0.8,
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <Box className="preferences-quiz-modal__card">
                    {/* Background Image */}
                    <Box
                      className="preferences-quiz-modal__card-image"
                      sx={{
                        backgroundImage: `url(${currentItem.imageUrl})`,
                      }}
                    />

                    {/* Content Overlay */}
                    <Box className="preferences-quiz-modal__card-overlay">
                      <Box className="preferences-quiz-modal__card-content">
                        {/* Price indicator */}
                        <Box className="preferences-quiz-modal__price-indicator">
                          {currentItem.priceRange}
                        </Box>

                        {/* Venue Info */}
                        <Typography variant="h5" className="preferences-quiz-modal__venue-name">
                          {currentItem.name}
                        </Typography>

                        <Typography variant="body2" className="preferences-quiz-modal__venue-neighborhood">
                          {currentItem.neighborhood}
                        </Typography>

                        {/* Tags */}
                        <Box className="preferences-quiz-modal__tags">
                          <Box className="preferences-quiz-modal__tag preferences-quiz-modal__tag--archetype">
                            {currentItem.archetype}
                          </Box>
                          {currentItem.vibe.slice(0, 2).map((vibe: any, index: number) => (
                            <Box key={index} className="preferences-quiz-modal__tag preferences-quiz-modal__tag--vibe">
                              {vibe}
                            </Box>
                          ))}
                        </Box>

                        <Typography variant="body2" className="preferences-quiz-modal__venue-description">
                          {currentItem.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              </AnimatePresence>
            ) : null}
          </Box>

          {/* Action Buttons */}
          {!itemsQuery.isLoading && !isCompleted() && currentItem && (
            <Box className="preferences-quiz-modal__actions">
              <motion.button
                className="preferences-quiz-modal__action-button preferences-quiz-modal__action-button--dislike"
                onClick={handleDislike}
                disabled={isAnimating || isSaving}
                whileHover={!isAnimating && !isSaving ? { scale: 1.1 } : {}}
                whileTap={!isAnimating && !isSaving ? { scale: 0.95 } : {}}
                aria-label="Dislike venue"
                style={{ opacity: (isAnimating || isSaving) ? 0.6 : 1 }}
              >
                {isSaving ? <CircularProgress size={20} /> : <XIcon />}
              </motion.button>

              <motion.button
                className="preferences-quiz-modal__action-button preferences-quiz-modal__action-button--skip"
                onClick={handleSkip}
                disabled={isAnimating || isSaving}
                whileHover={!isAnimating && !isSaving ? { scale: 1.05 } : {}}
                whileTap={!isAnimating && !isSaving ? { scale: 0.95 } : {}}
                aria-label="Skip venue"
                style={{ opacity: (isAnimating || isSaving) ? 0.6 : 1 }}
              >
                {isSaving ? <CircularProgress size={16} /> : 'Skip'}
              </motion.button>

              <motion.button
                className="preferences-quiz-modal__action-button preferences-quiz-modal__action-button--like"
                onClick={handleLike}
                disabled={isAnimating || isSaving}
                whileHover={!isAnimating && !isSaving ? { scale: 1.1 } : {}}
                whileTap={!isAnimating && !isSaving ? { scale: 0.95 } : {}}
                aria-label="Like venue"
                style={{ opacity: (isAnimating || isSaving) ? 0.6 : 1 }}
              >
                {isSaving ? <CircularProgress size={20} /> : <HeartFilledIcon />}
              </motion.button>
            </Box>
          )}

          {/* Instructions */}
          {!itemsQuery.isLoading && !isCompleted() && currentItem && (
            <Typography variant="caption" className="preferences-quiz-modal__instructions">
              Swipe right to like • Swipe left to dislike • Swipe up to skip
            </Typography>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

export default PreferencesQuizModal;